import React, { useEffect, useMemo, useRef, useState } from "react";

import { Dialog } from "@fronterahealth/frontera-ui-components";

import type { CreateAssessmentInputWithPRInput } from "@api/rest/types-and-hooks";
import { useCreateAssessmentInputWithPr, useListAssessmentFileKinds } from "@api/rest/types-and-hooks";
import { FormInput } from "@components/forms/FormInput";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError } from "@components/notifications/notifications";
import { PROpenedDialog } from "@pages/Prompts/PROpenedDialog";

interface NewAssessmentInputDialogProps {
  isDialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  /** Called after the PR successfully opens so the caller can invalidate
   *  any cached input lists. */
  onSubmitted?: () => void;
}

interface FormShape {
  key: string;
  file_kind: string;
}

const _DEFAULT_VALUES: FormShape = { key: "", file_kind: "" };

// Mirror of the backend regex in ``_CreateAssessmentInputWithPRInputSerializer``
// (``prompt_pr_views.py``). Enforced client-side so users see the error
// inline instead of round-tripping to a 400.
const KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

/**
 * Dialog for registering a new assessment input.
 *
 * Two fields:
 *  - ``file_kind`` (primary) — picked from a dropdown of the actual
 *    ``ApiLearnerFileFileKindChoices`` FDM enum. Fetched from
 *    ``list_assessment_file_kinds``; no more "type it and hope
 *    pipelines CI accepts it" — the enum member is guaranteed to
 *    exist.
 *  - ``key`` — the ``assessments.<key>`` identifier used in prompt
 *    yamls. Defaults to lowercased ``file_kind`` on selection, but
 *    editable so a caller can create semantically-named inputs (e.g.
 *    ``combined_vineland`` keyed off a ``VINELAND`` upload).
 *
 * Backend derives ``class_name`` + ``description`` from the key and
 * opens paired PRs (pipelines + api-django) that touch ``inputs.yaml``,
 * ``catalog.py``, and the regenerated registry mirror.
 */
export const NewAssessmentInputDialog: React.FC<NewAssessmentInputDialogProps> = ({
  isDialogOpen,
  setDialogOpen,
  onSubmitted,
}) => {
  const mutation = useCreateAssessmentInputWithPr();
  // Fetch the enum only while the dialog is open — no need to hit the
  // endpoint eagerly across the whole SPA.
  const { data: fileKindsResp, isLoading: fileKindsLoading } = useListAssessmentFileKinds({
    query: { enabled: isDialogOpen },
  });
  const fileKinds = useMemo<string[]>(() => {
    if (fileKindsResp?.status !== 200) return [];
    return (fileKindsResp.data.items ?? []).map((i) => i.key);
  }, [fileKindsResp]);

  const [openedUrls, setOpenedUrls] = useState<{
    pipelines: string;
    apiDjango: string;
  } | null>(null);
  // Remembers the last value we auto-wrote into ``key`` from a
  // file_kind pick. On the next file_kind change we only overwrite
  // ``key`` if it still equals that value — meaning the user hasn't
  // typed anything custom in between. This replaces an earlier
  // "onChange handler on FormInput" approach that clobbered
  // react-hook-form's own onChange and dropped every keystroke.
  const lastAutoKeyRef = useRef<string>("");

  const { formState, reset, onSubmit, register, watch, setValue } = useFormUtils<FormShape>({
    defaultValues: _DEFAULT_VALUES,
    mutationFn: async (values) => {
      const key = (values.key ?? "").trim();
      const file_kind = (values.file_kind ?? "").trim();
      if (!KEY_PATTERN.test(key)) {
        notifyError("Key must be lowercase snake_case (e.g. wnat_8), 2-64 chars.");
        return;
      }
      if (!file_kind) {
        notifyError("Pick a file kind from the dropdown.");
        return;
      }
      const payload: CreateAssessmentInputWithPRInput = { key, file_kind };
      try {
        const result = await mutation.mutateAsync({ data: payload });
        if (result.status === 201) {
          setDialogOpen(false);
          reset(_DEFAULT_VALUES);
          lastAutoKeyRef.current = "";
          onSubmitted?.();
          setOpenedUrls({
            pipelines: result.data.pipelines_pr_url,
            apiDjango: result.data.api_django_pr_url,
          });
        } else {
          const detail = (result.data as { detail?: string } | undefined)?.detail;
          notifyError(detail ?? `Failed to register assessment (status ${result.status}).`);
        }
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Failed to register assessment.");
      }
    },
  });

  // ``file_kind`` is managed via setValue (not native register), so tell
  // react-hook-form the field exists.
  useEffect(() => {
    register("file_kind");
  }, [register]);

  useEffect(() => {
    if (!isDialogOpen) {
      reset(_DEFAULT_VALUES);
      lastAutoKeyRef.current = "";
    }
  }, [isDialogOpen, reset]);

  const fileKindValue = watch("file_kind") ?? "";
  const keyValue = watch("key") ?? "";

  return (
    <>
      <Dialog
        title="Register new assessment input"
        open={isDialogOpen}
        setOpen={setDialogOpen}
        primaryButton={{
          text: mutation.isPending ? "Opening PR..." : "Register + Open PR",
          onClick: onSubmit,
          disabled: mutation.isPending || formState.isSubmitting,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setDialogOpen(false),
        }}
        size="lg"
      >
        <FormContainer onSubmit={onSubmit}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-secondary">
              Opens paired PRs to register a new assessment input. Once merged, prompts can use it as an input.
            </p>

            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-text-secondary">
                File kind <span className="text-text-error-primary">*</span>
              </span>
              <select
                value={fileKindValue}
                onChange={(e) => {
                  const next = e.target.value;
                  setValue("file_kind", next, { shouldDirty: true });
                  // Suggest a matching key when the user hasn't
                  // customized it. "Not customized" = empty OR still
                  // equals whatever we auto-wrote last time — that way
                  // a user who typed a custom key doesn't have it
                  // overwritten when they change file_kind.
                  const currentKey = watch("key") ?? "";
                  if (!currentKey || currentKey === lastAutoKeyRef.current) {
                    const nextKey = next.toLowerCase();
                    setValue("key", nextKey, { shouldDirty: true });
                    lastAutoKeyRef.current = nextKey;
                  }
                }}
                disabled={fileKindsLoading}
                className="rounded-md border border-border-medium bg-bg-default px-3 py-2 text-sm text-text-primary"
              >
                <option value="">{fileKindsLoading ? "Loading…" : "Select a file kind…"}</option>
                {fileKinds.map((fk) => (
                  <option key={fk} value={fk}>
                    {fk}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-text-secondary">
                Key <span className="text-text-error-primary">*</span>
                <span className="ml-2 text-xs font-normal text-text-tertiary">
                  auto-derived; edit for a custom name
                </span>
              </span>
              {/* Direct ``FormInput`` (not the ``useFormUtils`` wrapper)
                  so keystrokes don't lose focus — see the sibling
                  NewPrompt dialog for the pattern. Do NOT pass
                  ``onChange`` here: ``FormInput`` spreads props after
                  ``register(...)``, so a custom onChange overwrites
                  react-hook-form's handler and every keystroke is
                  silently discarded. Dirty tracking runs off
                  ``lastAutoKeyRef`` on the file_kind onChange
                  instead. */}
              <FormInput<FormShape>
                register={register}
                formState={formState}
                formKey="key"
                required
                placeholder="wnat_8"
              />
              {keyValue && !KEY_PATTERN.test(keyValue) && (
                <span className="text-xs text-text-error-primary">
                  Must be lowercase snake_case, 2-64 chars, starting with a letter.
                </span>
              )}
            </div>
          </div>
        </FormContainer>
      </Dialog>
      <PROpenedDialog
        pipelinesUrl={openedUrls?.pipelines ?? null}
        apiDjangoUrl={openedUrls?.apiDjango ?? null}
        onClose={() => setOpenedUrls(null)}
      />
    </>
  );
};
